/**
 * Real-Time Unit Economics from Trace Streams (Patent Sub-system R)
 * Sub-second cost-per-customer / cost-per-feature joined from OpenTelemetry.
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Helmet } from 'react-helmet-async';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Loader2, FlaskConical, Activity, Users, Layers } from 'lucide-react';
import DemoDataBanner from '../components/DemoDataBanner';
import BrandTitle from '../components/BrandTitle';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtUsd = (n) => `$${(n || 0).toLocaleString('en-US', { maximumFractionDigits: 2 })}`;
const fmtUsdSmall = (n) => `$${(n || 0).toFixed(6)}`;

const UnitEcon = () => {
  const [customers, setCustomers] = useState([]);
  const [features, setFeatures] = useState([]);
  const [sample, setSample] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      axios.get(`${API_URL}/unit-econ/customer-rollup`),
      axios.get(`${API_URL}/unit-econ/feature-rollup`),
      axios.get(`${API_URL}/unit-econ/demo-stream?limit=15`),
    ]).then(([c, f, s]) => {
      setCustomers(c.data.customers || []);
      setFeatures(f.data.features || []);
      setSample(s.data.sample || []);
    }).finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100" data-testid="unit-econ-page">
      <Helmet><title>Real-Time Unit Economics | CARTIEAI</title></Helmet>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
        <DemoDataBanner />
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 text-[11px] font-mono uppercase tracking-widest text-cyan-300 border border-emerald-500/30 rounded-md px-2 py-1 mb-5" data-testid="ue-patent-badge">
            <FlaskConical className="w-3 h-3" /> Proprietary engine
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight leading-[1.05]"><BrandTitle>Real-Time Unit Economics</BrandTitle></h1>
          <p className="mt-4 text-base sm:text-lg text-slate-300 leading-relaxed">
            Cloudability and Vantage are batch (daily). <span className="text-cyan-300">We compute cost-per-customer / cost-per-feature in REAL-TIME</span> from OpenTelemetry trace streams.
          </p>
          <p className="mt-3 text-sm text-slate-500">
            Sub-second streaming-join of trace spans × per-resource cost rates × per-trace customer-id → emits (customer, feature, cost, latency) tuples within ms of request completion.
          </p>
        </div>

        {loading && <Loader2 className="w-8 h-8 animate-spin mx-auto my-12 text-slate-500" />}

        {!loading && (
          <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="bg-slate-900/60 border border-white/10 shadow-sm border-white/10">
              <CardHeader>
                <CardTitle className="text-white text-lg flex items-center gap-2">
                  <Users className="w-5 h-5 text-cyan-300" /> Per-customer rollup
                </CardTitle>
                <CardDescription>24h actual cost · projected monthly · gross margin vs MRR.</CardDescription>
              </CardHeader>
              <CardContent>
                <table className="w-full text-sm" data-testid="ue-customer-table">
                  <thead className="text-[10px] uppercase tracking-widest text-slate-500">
                    <tr className="border-b border-white/10">
                      <th className="text-left p-2">Customer</th>
                      <th className="text-right p-2">MRR</th>
                      <th className="text-right p-2">$/mo</th>
                      <th className="text-right p-2">Margin</th>
                    </tr>
                  </thead>
                  <tbody>
                    {customers.map((c) => (
                      <tr key={c.customer_id} className="border-b border-white/5 hover:bg-slate-950/60">
                        <td className="p-2 text-xs text-white">{c.customer_name}</td>
                        <td className="p-2 text-xs text-right text-slate-300 font-mono">{fmtUsd(c.mrr_usd)}</td>
                        <td className="p-2 text-xs text-right text-cyan-300 font-mono">{fmtUsd(c.projected_monthly_cost_usd)}</td>
                        <td className={`p-2 text-xs text-right font-mono ${c.gross_margin_pct >= 95 ? 'text-emerald-300' : c.gross_margin_pct >= 80 ? 'text-amber-300' : 'text-red-300'}`}>{c.gross_margin_pct}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>

            <Card className="bg-slate-900/60 border border-white/10 shadow-sm border-white/10">
              <CardHeader>
                <CardTitle className="text-white text-lg flex items-center gap-2">
                  <Layers className="w-5 h-5 text-cyan-300" /> Per-feature cost
                </CardTitle>
                <CardDescription>Cost-per-request observed over the last 24h.</CardDescription>
              </CardHeader>
              <CardContent>
                <table className="w-full text-sm" data-testid="ue-feature-table">
                  <thead className="text-[10px] uppercase tracking-widest text-slate-500">
                    <tr className="border-b border-white/10">
                      <th className="text-left p-2">Feature</th>
                      <th className="text-right p-2">Requests</th>
                      <th className="text-right p-2">$/req</th>
                      <th className="text-right p-2">24h cost</th>
                    </tr>
                  </thead>
                  <tbody>
                    {features.map((f) => (
                      <tr key={f.feature} className="border-b border-white/5 hover:bg-slate-950/60">
                        <td className="p-2 text-xs text-white">{f.feature}</td>
                        <td className="p-2 text-xs text-right text-slate-300 font-mono">{f.requests_24h}</td>
                        <td className="p-2 text-xs text-right text-cyan-300 font-mono">{fmtUsdSmall(f.cost_per_request_usd)}</td>
                        <td className="p-2 text-xs text-right text-white font-mono">{fmtUsd(f.cost_24h_usd)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          </div>
        )}

        {!loading && sample.length > 0 && (
          <Card className="mt-8 bg-slate-900/60 border border-white/10 shadow-sm border-white/10">
            <CardHeader>
              <CardTitle className="text-white text-lg flex items-center gap-2">
                <Activity className="w-5 h-5 text-cyan-300" /> Live trace stream sample
              </CardTitle>
              <CardDescription>Streaming-join output: 1 row per completed request.</CardDescription>
            </CardHeader>
            <CardContent>
              <table className="w-full text-sm" data-testid="ue-trace-stream">
                <thead className="text-[10px] uppercase tracking-widest text-slate-500">
                  <tr className="border-b border-white/10">
                    <th className="text-left p-2">Trace</th>
                    <th className="text-left p-2">Customer</th>
                    <th className="text-left p-2">Feature</th>
                    <th className="text-right p-2">Cost</th>
                    <th className="text-right p-2">Latency</th>
                  </tr>
                </thead>
                <tbody>
                  {sample.map((t) => (
                    <tr key={t.trace_id} className="border-b border-white/5">
                      <td className="p-2 text-[10px] text-slate-500 font-mono">{t.trace_id.slice(0, 12)}</td>
                      <td className="p-2 text-xs text-slate-200 font-mono">{t.customer_id}</td>
                      <td className="p-2 text-xs text-white">{t.feature}</td>
                      <td className="p-2 text-xs text-right text-cyan-300 font-mono">{fmtUsdSmall(t.cost_usd)}</td>
                      <td className="p-2 text-xs text-right text-slate-300 font-mono">{t.duration_ms}ms</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default UnitEcon;
